import React from 'react';
import { User, Gavel, FileText, Clock, TrendingUp } from 'lucide-react';
import PropTypes from 'prop-types';
import { DocketPropType } from '../../../types/propTypes';
import { getStatusColor } from '../../../utils/caseStatusUtils';
import { formatDateForDisplay, formatDateOnly } from '../../../utils/dateUtils';

const CaseCard = ({
  caseData,
  onSelect,
  onToggleSelection,
  isSelected = false,
  isSourceCase = false
}) => {
  // const getTotalSentence = (counts) => {
  //   if (!counts || counts.length === 0) return 'No sentence';

  //   const totalMonths = counts.reduce((total, count) => {
  //     if (!count.sentence) return total;
  //     const { value, unit } = count.sentence;
  //     if (unit === 'months') return total + value;
  //     if (unit === 'years') return total + value * 12;
  //     if (unit === 'days') return total + value / 30.44;
  //     return total;
  //   }, 0);

  //   if (totalMonths === 0) return 'No sentence';
  //   if (totalMonths < 12) return `${totalMonths.toFixed(1)} months`;
  //   return `${(totalMonths / 12).toFixed(1)} years`;
  // };

  const getSentencingSummary = (caseData) => {
    const parts = [];

    // Check if all charges are dismissed
    if (caseData.isDismissed) {
      return 'Dismissed';
    }

    // Add confinement if present
    if (
      caseData.totalConfinementMonths &&
      caseData.totalConfinementMonths > 0
    ) {
      const years = caseData.totalConfinementMonths / 12;
      const months = caseData.totalConfinementMonths % 12;
      let confinementText = '';
      if (years >= 1) {
        confinementText = `${Math.floor(years)} year${Math.floor(years) > 1 ? 's' : ''}`;
        if (months > 0)
          confinementText += ` ${months} month${months > 1 ? 's' : ''}`;
      } else {
        confinementText = `${months} month${months > 1 ? 's' : ''}`;
      }
      parts.push(confinementText);
    }

    // Add confinement days if present
    if (caseData.totalConfinementDays && caseData.totalConfinementDays > 0) {
      parts.push(
        `${caseData.totalConfinementDays} day${caseData.totalConfinementDays > 1 ? 's' : ''}`
      );
    }

    // Add fines if present
    if (caseData.totalFines && caseData.totalFines > 0) {
      parts.push(`$${caseData.totalFines.toFixed(2)} fine`);
    }

    // Add probation if present
    if (caseData.totalProbationMonths && caseData.totalProbationMonths > 0) {
      const years = caseData.totalProbationMonths / 12;
      const months = caseData.totalProbationMonths % 12;
      let probationText = '';
      if (years >= 1) {
        probationText = `${Math.floor(years)} year${Math.floor(years) > 1 ? 's' : ''}`;
        if (months > 0)
          probationText += ` ${months} month${months > 1 ? 's' : ''}`;
      } else {
        probationText = `${months} month${months > 1 ? 's' : ''}`;
      }
      parts.push(`${probationText} probation`);
    }

    // Add diversion if present
    if (caseData.hasDiversion) {
      parts.push('Diversion');
    }

    // Add fugitive extradition if present
    if (
      caseData.sentencingTypes &&
      caseData.sentencingTypes.includes('Fugitive Extradition')
    ) {
      parts.push('Fugitive Extradition');
    }

    if (parts.length === 0) {
      return 'No sentence';
    }

    return parts.join(', ');
  };

  const getSentencingTypes = (caseData) => {
    const types = [];
    if (
      caseData.totalConfinementMonths &&
      caseData.totalConfinementMonths > 0
    ) {
      types.push('Confinement');
    }
    if (caseData.totalConfinementDays && caseData.totalConfinementDays > 0) {
      types.push('Confinement');
    }
    if (caseData.totalFines && caseData.totalFines > 0) {
      types.push('Fine');
    }
    if (caseData.totalProbationMonths && caseData.totalProbationMonths > 0) {
      types.push('Probation');
    }
    if (caseData.hasDiversion) {
      types.push('Diversion');
    }
    if (caseData.isDismissed) {
      types.push('Dismissed');
    }
    if (
      caseData.sentencingTypes &&
      caseData.sentencingTypes.includes('Fugitive Extradition')
    ) {
      types.push('Fugitive Extradition');
    }
    return types;
  };

  const getJudgeInfo = (judges) => {
    if (!judges || judges.length === 0) {
      return {
        display: 'No judge assigned',
        isPotential: false
      };
    }

    const realJudges = judges.filter(
      (judge) => judge.firstName && judge.firstName !== 'Department'
    );

    if (realJudges.length === 0) {
      return {
        display: 'No judge assigned',
        isPotential: false
      };
    }

    const judge = realJudges[0];
    const judgeName = `${judge.firstName} ${judge.lastName}`;

    if (judge.isPotential) {
      return {
        display: `(${judgeName})`,
        isPotential: true
      };
    }

    return {
      display: judgeName,
      isPotential: false
    };
  };

  const getChargeSummary = (counts) => {
    if (!counts || counts.length === 0) return 'No charges';

    const primaryCharge = counts[0];
    const totalCharges = counts.length;

    return (
      <div className="space-y-1">
        <div className="font-medium text-gray-900">
          {primaryCharge.chargeCode}
        </div>
        {totalCharges > 1 && (
          <div className="text-sm text-gray-500">
            +{totalCharges - 1} more charges
          </div>
        )}
      </div>
    );
  };

  const formatDate = (dateString) => {
    return formatDateForDisplay(dateString);
  };

  // const generateCaseSummary = (caseData) => {
  //   const judgeInfo = getJudgeInfo(caseData.judges);
  //   const defendants =
  //     caseData.defendants
  //       ?.map((d) => `${d.firstName} ${d.lastName}`)
  //       .join(', ') || 'N/A';
  //   const charges =
  //     caseData.counts?.map((c) => c.chargeCode).join(', ') || 'No charges';
  //   const sentence = getTotalSentence(caseData.counts);

  //   return `Case: ${caseData.caseNumber}
  // Jurisdiction: ${caseData.jurisdiction}
  // Courthouse: ${caseData.courthouse}
  // Filed: ${formatDate(caseData.openDate)}
  // Closed: ${formatDate(caseData.closeDate)}
  // Duration: ${caseData.durationDays ? `${caseData.durationDays} days` : 'N/A'}
  // Judge: ${judgeInfo.display}
  // Defendants: ${defendants}
  // Charges: ${charges}
  // Sentence: ${sentence}
  // Status: ${caseData.caseStatus || 'N/A'}

  // Data from Briefed`;
  // };

  return (
    <div
      className={`rounded-lg border-2 transition-all duration-200 cursor-pointer group ${
        isSelected
          ? 'border-blue-500 bg-blue-50 shadow-lg'
          : isSourceCase
            ? 'bg-orange-50 border-orange-300 hover:border-orange-400 hover:shadow-lg shadow-orange-200'
            : 'bg-white border-gray-200 hover:border-blue-300 hover:shadow-md'
      }`}
      onClick={() => onSelect(caseData)}
    >
      <div className="p-4 space-y-3 relative">
        {/* Header with Selection Button */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors break-all">
              {caseData.caseNumber}
            </h3>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {isSourceCase && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-300">
                  Source Case
                </span>
              )}
              <span
                className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(caseData.caseStatus)}`}
              >
                {caseData.caseStatus || 'Unknown'}
              </span>
              {caseData._similarityScore && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200">
                  {Math.round(caseData._similarityScore * 100)}% similar
                </span>
              )}
            </div>
          </div>

          {/* Selection Button - Plus/Minus in top-right corner */}
          {onToggleSelection && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggleSelection(caseData);
              }}
              className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-all z-10 ${
                isSelected
                  ? 'bg-blue-600 text-white shadow-md hover:bg-blue-700'
                  : 'bg-white text-blue-600 border border-blue-300 hover:border-blue-500 hover:bg-blue-50'
              }`}
              title={isSelected ? 'Remove from analytics' : 'Add to analytics'}
            >
              {isSelected ? (
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20 12H4"
                  />
                </svg>
              ) : (
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  />
                </svg>
              )}
            </button>
          )}
        </div>

        {/* Key Information - Each on separate line */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Clock className="w-4 h-4" />
            <span className="font-medium">Filed:</span>
            <span className="text-sm text-gray-900">
              {formatDate(caseData.openDate)}
            </span>
          </div>

          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Gavel className="w-4 h-4" />
            <span className="font-medium">Judge:</span>
            <span className="text-sm text-gray-900">
              {getJudgeInfo(caseData.judges).display}
            </span>
          </div>
        </div>

        {/* Charges */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <FileText className="w-4 h-4" />
            <span className="font-medium">Charges:</span>
          </div>
          <div className="pl-6">{getChargeSummary(caseData.counts)}</div>
        </div>

        {/* Sentence Information */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <TrendingUp className="w-4 h-4" />
            <span className="font-medium">Sentence:</span>
          </div>
          <div className="pl-6">
            <div className="text-sm font-medium text-gray-900">
              {getSentencingSummary(caseData)}
            </div>
            {getSentencingTypes(caseData).length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {getSentencingTypes(caseData).map((type, index) => (
                  <span
                    key={index}
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      type === 'Confinement'
                        ? 'bg-red-50 text-red-700 border border-red-200'
                        : type === 'Probation'
                          ? 'bg-blue-50 text-blue-700 border border-blue-200'
                          : type === 'Fine'
                            ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                            : type === 'Dismissed'
                              ? 'bg-gray-50 text-gray-700 border border-gray-200'
                              : type === 'Fugitive Extradition'
                                ? 'bg-purple-50 text-purple-700 border border-purple-200'
                                : 'bg-green-50 text-green-700 border border-green-200'
                    }`}
                  >
                    {type}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Defendants */}
        {caseData.defendants && caseData.defendants.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <User className="w-4 h-4" />
              <span className="font-medium">Defendants:</span>
            </div>
            <div className="pl-6">
              <div className="text-sm text-gray-900 space-y-1">
                {caseData.defendants.map((defendant, index) => (
                  <div key={index}>
                    <div className="font-medium">
                      {defendant.firstName} {defendant.lastName}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      Date of Birth:{' '}
                      {defendant.dateOfBirth
                        ? formatDateOnly(defendant.dateOfBirth) || 'N/A'
                        : 'N/A'}{' '}
                      • Race: {defendant.race || 'N/A'}
                      {defendant.gender && ` • Gender: ${defendant.gender}`}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Match Reasons (for similarity mode) */}
        {caseData._matchReasons && caseData._matchReasons.length > 0 && (
          <div className="border-t border-gray-100 pt-3 mt-3">
            <div className="text-xs text-gray-500 font-medium mb-2">
              Why this case is similar:
            </div>
            <div className="flex flex-wrap gap-1">
              {caseData._matchReasons.map((reason, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-200"
                >
                  {reason}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

CaseCard.propTypes = {
  caseData: DocketPropType,
  onSelect: PropTypes.func.isRequired,
  onToggleSelection: PropTypes.func,
  isSelected: PropTypes.bool,
  isSourceCase: PropTypes.bool
};

export default CaseCard;
